 
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
    NSTable,
    NSCellInput,
    NSSelect,
} from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatISOStartOfMonth } from 'ns_libs/formatter';
import {
    OPERATIONS_INCOME_WS_HANDLERS, CASH_FLOW_TOOLTIP_MESSAGE,
    RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT,
} from 'views/ProFormaTable/constants';
import { ProFormaModelIds } from 'constants/proForma';
import { IFormattedCashFlowPeriod, ITenant } from 'views/ProFormaTable/types';
import { doesRentableSFExceedBuiltGSF } from 'views/ProFormaTable/helpers';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import { useAutoFillDates } from '../../hooks/useAutoFillDates';
import PeriodsSelect from '../../../../PeriodsSelect/PeriodsSelect';
import { RENT_STEP_OPTIONS } from '../../constants';
import './TenantsTable.scss';

export interface ITenantsTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
    monthlyPeriodOptions: IFormattedCashFlowPeriod[];
}

const TenantsTable = ({
    operationBeginIndex, baseCashFlowColumns, monthlyPeriodOptions,
}: ITenantsTableProps) => {
    const {
        incomeSummary, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma,
        socketRef, roomId,
    } = useProFormaSocketContext();

    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const rentalIncomeTotals = isApartmentWithRetail ? incomeSummary.totalRetailIncome : incomeSummary.totalRentalIncome;

    const handleUpdateTenant = (id: number, field: string) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.TENANT.UPDATE, id, field);

    const shouldShowRSFWarning = isApartmentWithRetail
        ? doesRentableSFExceedBuiltGSF(Number(proForma.retailBuiltGrossSquareFootage), incomeSummary.totalRetailIncome?.totalSquareFootage)
        : doesRentableSFExceedBuiltGSF(
            Number(proForma.builtGrossSquareFootage), incomeSummary.totalRentableSf,
        );

    // Auto-fill recoveryStart for Tenants that don't have it set
    useAutoFillDates({
        items: incomeSummary?.incomeTenants,
        shouldAutoFill: tenant => !!tenant.rentStart && !tenant.recoveryStart,
        getItemId: tenant => tenant.id,
        getValueToFill: tenant => tenant.rentStart,
        fieldName: 'recoveryStart',
        wsHandler: OPERATIONS_INCOME_WS_HANDLERS.TENANT.UPDATE,
        socketRef,
        roomId,
    });

    const tenantCashFlowColumns: ColumnDef<ITenant>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `tenant-period-${column.id}`,
        accessorFn: (row, tenantIndex: number) => {
            const hasCashFlow = row.rentStartMonth
                && typeof operationsCashFlow?.[row.rentStartMonth]?.incomeTenants?.[tenantIndex]?.value === 'number';
            return (
                hasCashFlow
                    ? (operationsCashFlow?.[cashFlowIndex]?.incomeTenants?.[tenantIndex]?.value || 0)
                    : null
            );
        },
        footer: () => (
            <span className="text-dark">
                {operationBeginIndex
                    ? formatDisplayValue(
                        operationsCashFlow?.[cashFlowIndex]?.[isApartmentWithRetail ? 'totalRetailIncome' : 'totalRentalIncome'] || 0,
                        showCents, 'currency',
                    )
                    : ' '}
            </span>
        ),
    }));

    const tenantColumns: ColumnDef<ITenant>[] = [
        {
            accessorKey: 'suite',
            id: 'suite',
            header: () => <span className="pl-1">Suite</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="text"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'suite');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    placeholder="Enter value"
                />
            ),
            footer: () => <span className="pl-1 text-nowrap">Total rental income</span>,
        },
        {
            accessorKey: 'tenant',
            id: 'tenant',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <span>Tenant</span>
                    <FontAwesomeIcon
                        id="plus-icon"
                        icon={faPlus}
                        className="ml-2 text-muted cursor--pointer"
                        onClick={() => handleCreateOperationsRow(
                            OPERATIONS_INCOME_WS_HANDLERS.TENANT.CREATE,
                            { suite: 'Suite', tenant: 'New tenant' },
                        )}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <div className="w-100 ml-n1">
                        <NSCellInput
                            type="text"
                            value={getValue()}
                            handleUpdateValue={updatedValue => {
                                const updateFn = handleUpdateTenant(Number(row.original.id), 'tenant');
                                if (typeof updateFn === 'function') {
                                    updateFn({
                                        target: { value: updatedValue },
                                    } as React.ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            placeholder="Enter value"
                        />
                    </div>
                    <FontAwesomeIcon
                        id="trash-icon"
                        icon={faTrash}
                        className="ml-1 text-dark cursor--pointer"
                        onClick={() => handleDeleteOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.TENANT.DELETE, Number(row.original.id))}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'leaseStart',
            id: 'leaseStart',
            header: () => <span>Lease start</span>,
            cell: ({ row, getValue }) => (
                <PeriodsSelect
                    compact
                    hasInvertedColors
                    handleChange={value => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'leaseStart');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    options={monthlyPeriodOptions}
                    value={!getValue() ? null
                        : formatISOStartOfMonth(getValue() as string)}
                />
            ),
        },
        {
            accessorKey: 'rentStart',
            id: 'rentStart',
            header: () => <span>Rent start</span>,
            cell: ({ row, getValue }) => (
                <PeriodsSelect
                    compact
                    hasInvertedColors
                    handleChange={value => {
                        // Update rent start - backend will automatically set recovery_start if not already set
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'rentStart');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    options={monthlyPeriodOptions}
                    value={!getValue() ? null
                        : formatISOStartOfMonth(getValue() as string)}
                />
            ),
        },
        {
            accessorKey: 'rentGrowth',
            id: 'rentGrowth',
            header: () => <span>Rent growth</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'rentGrowth');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
        },
        {
            accessorKey: 'rentStep',
            id: 'rentStep',
            header: () => <span>Rent step</span>,
            cell: ({ row, getValue }) => (
                <div className="d-flex justify-content-end w-100">
                    <NSSelect
                        name={`${row.id}-rentStep-select`}
                        formClassName="w-100"
                        options={RENT_STEP_OPTIONS}
                        value={RENT_STEP_OPTIONS.find(opt => opt.value === getValue())}
                        onChange={(option: any) => {
                            const updateFn = handleUpdateTenant(Number(row.original.id), 'rentStep');
                            if (typeof updateFn === 'function') {
                                const input = document.createElement('input');
                                input.value = option.value;
                                updateFn({
                                    target: input,
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isClearable={false}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        small
                        styles={{
                            ...customStyles,
                            control: (provided: any) => ({
                                ...provided,
                                backgroundColor: '#4F5B69',
                                width: '100%',
                                border: 'none',
                                minHeight: '30px',
                                paddingRight: '4px',
                            }),
                            singleValue: (provided: any) => ({
                                ...provided,
                                color: '#e3eaef',
                                paddingRight: '4px',
                                overflow: 'visible',
                            }),
                            menuList: (provided: any) => ({
                                ...provided,
                                width: '100px',
                            }),
                        }}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'squareFootage',
            id: 'squareFootage',
            header: () => (
                <div className={classNames({ 'd-flex': shouldShowRSFWarning })}>
                    <RenderIf isTrue={shouldShowRSFWarning}>
                        <IconWithTooltip
                            name={`RSFDiscrepancyIcon-${proForma.proFormaModelId}`}
                            icon={faTriangleExclamation}
                            tooltipText={RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT}
                            iconClassName="text-warning"
                            className="my-auto mx-1"
                        />
                    </RenderIf>
                    <span>RSF</span>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'squareFootage');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => <span>{formatDisplayValue(rentalIncomeTotals?.totalSquareFootage || 0, false)}</span>,
        },
        {
            accessorKey: 'valuePerSfPerYear',
            id: 'valuePerSfPerYear',
            header: () => <span>$/RSF/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'valuePerSfPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(rentalIncomeTotals?.totalValuePerSfPerYear || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'annualRent',
            id: 'annualRent',
            header: () => (
                <div className="pr-1">
                    <span>Annual rent</span>
                    <span className="text-nowrap"> (yr 1)</span>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdateTenant(Number(row.original.id), 'annualRent');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(rentalIncomeTotals?.totalAnnualRent || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...tenantCashFlowColumns,
    ];

    const table = useReactTable({
        data: incomeSummary.incomeTenants || [],
        columns: tenantColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable TenantsTable mb-1">
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={9} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added tenants will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Tenant</span>
                                {' '}
                                header to add your first tenant.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`tenant-period-${column.id}`}
                                className="NSTable__tbody__tr__td"
                                id={`tenant-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target={`tenant-placeholder-period-${index}`}>
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default TenantsTable;
